import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import produce from "immer";

import { recordActionAsEnum, recordActionFromEnum } from "psims/models/api/data-submission-record-action";
import { UseMsoAnnualActivityRefData } from "../use-mso-annual-activity-ref-data";
import { UseFocusedField } from "psims/react/util/use-focused-field";
import { FieldValidation, FocusField } from "../types";
import useTrackChanges from "../../../shared/use-track-changes";
import { cleanSubmissionEntityList } from "../../../shared/update-data-utils";
import { isEmpty } from "psims/lib/empty";
import { MsoStockholdingStorage } from "../types";
import { validateStorage, StorageValidationResult } from "./mso-stockholding-storages-validation";
import { getStorageNotificationMessage } from "./mso-stockholding-storages-notifications";
import { getStorageInlineMessage } from "./mso-stockholding-storages-inline-messages";
import type { UpdateMsoStockholdingStorageVM } from "psims/gen/xapi-client/models/UpdateMsoStockholdingStorageVM";

// Produces unique string keys like "row_1", "row_2" — only used for React key={} props.
let _keyCounter = 0;
const newKey = () => `row_${++_keyCounter}`;

/** Stamp raw server records with _id (Symbol) and _key (string). */
function stampRecords(
  raw: Array<MsoStockholdingStorage>,
  keyMap: Map<number, string>
): MsoStockholdingStorage[] {
  return (raw || []).map(s => {
    if (s.id != null && !keyMap.has(s.id)) {
      keyMap.set(s.id, newKey());
    }
    const stableKey = s.id != null ? keyMap.get(s.id)! : newKey();
    return {
      ...s,
      _id: Symbol(),    // unique per session — used for findIndex / focus matching
      _key: stableKey,  // stable string — used as React key={}
      recordAction: s.recordAction ?? undefined,
    };
  });
}

interface ProgressController {
  currentStep: { kind: 'data' | 'submit' };
  goToStep: (index: number) => any;
}

interface UseMsoStockholdingStoragesProps {
  dataSubmissionId: number;
  focusFieldCtrl: UseFocusedField<FocusField>;
  initialStorages: Array<MsoStockholdingStorage>;
  progressCtrl: ProgressController;
  refData: UseMsoAnnualActivityRefData;
}

// ─── addStorage params ────────────────────────────────────────────────────────

interface AddStorageParams {
  msoProductId: number;
  storageMsoFuelCategory: number;
}

function useMsoStockholdingStorages({
  dataSubmissionId,
  focusFieldCtrl,
  initialStorages,
  progressCtrl,
  refData,
}: UseMsoStockholdingStoragesProps) {

  // keyMapRef maps DB id → stable _key string across re-renders.
  const keyMapRef = useRef<Map<number, string>>(new Map());

  // Seed state once on mount. We do NOT put initialStorages in a useMemo that
  // runs every render — that's what caused the infinite loop (new Symbols →
  // new array reference → effect fires → setState → render → repeat).
  const [data, setData] = useState<MsoStockholdingStorage[]>(() =>
    stampRecords(initialStorages, keyMapRef.current)
  );

  // Track the serialised ID list from the server so we only re-sync when the
  // actual server data changes (new/deleted records), not on every render.
  const serverIdSignature = useMemo(() =>
    (initialStorages || []).map(s => s.id ?? 'new').join(','),
    [initialStorages]
  );
  const prevSignatureRef = useRef(serverIdSignature);

  useEffect(() => {
    if (serverIdSignature !== prevSignatureRef.current) {
      prevSignatureRef.current = serverIdSignature;
      setData(stampRecords(initialStorages, keyMapRef.current));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverIdSignature]); // intentionally exclude initialStorages — signature is the guard

  // Reset when user navigates away from the data step.
  const prevStepKindRef = useRef(progressCtrl.currentStep.kind);
  useEffect(() => {
    const kind = progressCtrl.currentStep.kind;
    if (kind !== 'data' && prevStepKindRef.current === 'data') {
      setData(stampRecords(initialStorages, keyMapRef.current));
    }
    prevStepKindRef.current = kind;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressCtrl.currentStep.kind]); // intentionally exclude initialStorages

  const changeCtrl = useTrackChanges({
    currentData: data,
    dataFields: [
      'storageName', 'terminalName', 'terminalLocation',
      'capacityReservedForCompany', 'capacityReservedForYou',
      'physicalStorageCapacity', 'terminalCoOwned',
      'receiveProducts', 'distributeProducts', 'terminalOperator',
      'terminalOwners'
    ],
    initialData: data, // changeCtrl compares against the seeded data, not a re-computed memo
  });

  /**
   * Add a new blank storage row.
   *
   * Accepts either the legacy `(msoProductId: number)` signature or the new
   * `({ msoProductId, storageMsoFuelCategory })` object so callers that haven't
   * been updated yet continue to work.
   */
  const addStorage = useCallback((
    params: number | AddStorageParams
  ) => {
    const msoProductId = typeof params === 'number' ? params : params.msoProductId;
    const storageMsoFuelCategory = typeof params === 'number' ? undefined : params.storageMsoFuelCategory;

    setData(prev => produce(prev, draft => {
      draft.push({
        _id: Symbol(),
        _key: newKey(),
        dataSubmissionId,
        msoProductId,
        storageMsoFuelCategory,
        storageName: '',
        terminalName: '',
        terminalLocation: '',
        capacityReservedForCompany: undefined,
        capacityReservedForYou: undefined,
        physicalStorageCapacity: undefined,
        receiveProducts: undefined,
        distributeProducts: undefined,
        terminalCoOwned: undefined,
        expiryDate: null,
        terminalOperator: undefined,
        nameOfOtherCompany: undefined,
        typeOfArrangementTxt: undefined,
        typeOfArrangementEntitledCapacityTxt: undefined,
        storagePriceThatWouldBeOffered: undefined,
        storagePriceOffered: undefined,
        recordAction: recordActionAsEnum('Create'),
      });
    }));
  }, [dataSubmissionId]);

  const deleteStorage = useCallback((storage: MsoStockholdingStorage) => {
    setData(prev => produce(prev, draft => {
      const idx = draft.findIndex(s => s._id === storage._id);
      if (idx === -1) return;
      if (draft[idx].id != null) {
        draft[idx] = { ...draft[idx], recordAction: recordActionAsEnum('Delete') };
      } else {
        draft.splice(idx, 1);
      }
    }));
  }, []);

  const deleteAllStoragesForProduct = useCallback((msoProductId: number) => {
    setData(prev =>
      prev
        .filter(d => d.id != null || d.msoProductId !== msoProductId)
        .map(d => d.msoProductId !== msoProductId ? d : { ...d, recordAction: recordActionAsEnum('Delete') })
    );
  }, []);

  const updateStorage = useCallback((storage: MsoStockholdingStorage) => {
    setData(prev => produce(prev, draft => {
      const idx = draft.findIndex(s => s._id === storage._id);
      if (idx === -1) return;
      draft[idx] = {
        ...storage,
        recordAction: recordActionAsEnum(storage.id == null ? 'Create' : 'Update'),
      };
    }));
  }, []);

  const dataView = useMemo(() => {
    let haveRecordedInactiveError = false;
    return data.map(s => {
      const product = refData.products.find(p => p.id === s.msoProductId);
      const productStatus = (product?.productStatus ?? 'active') as 'active' | 'inactive';
      const productName = product?.name ?? 'Unknown product';
      const validationResults = validateStorage(s, productStatus);

      const validations = validationResults.reduce((map, validationResult) => {
        // Only show one inactive-product error across all rows (mirrors imports behaviour)
        if (haveRecordedInactiveError && validationResult.key === 'delete') {
          return map;
        }

        const next = {
          ...map,
          [validationResult.key]: {
            inline: getStorageInlineMessage(validationResult),
            notification: {
              parts: getStorageNotificationMessage(
                validationResult.key,
                () => {
                  if (validationResult.code === 'inactive_product') {
                    if (progressCtrl.currentStep.kind === 'submit') {
                      progressCtrl.goToStep(1); // storage is step 1
                    }
                    focusFieldCtrl.setFocusedField({
                      field: 'delete',
                      kind: 'msoStockholdingStorages',
                      productId: s.msoProductId ?? 0,
                    });
                  } else {
                    focusFieldCtrl.setFocusedField({
                      _id: s._id,
                      field: validationResult.key,
                      kind: 'data',
                    });
                  }
                },
                productName,
                validationResult
              ),
            },
            validationResult,
          },
        };

        if (validationResult.key === 'delete') {
          haveRecordedInactiveError = true;
        }

        return next;
      }, {} as Record<string, any>);

      return { data: s, validations };
    });
  }, [data, focusFieldCtrl, progressCtrl, refData.products]);

  const requestEntities = useMemo(() =>
  cleanSubmissionEntityList(data).map(entity => {
    const { _key, ...rest } = entity;
    const mapped: UpdateMsoStockholdingStorageVM = {
      ...rest,
      expiryDate: rest.expiryDate instanceof Date
        ? rest.expiryDate.toISOString()
        : rest.expiryDate ?? undefined,
      terminalOwners: rest.terminalOwners ?? undefined,
      terminalOperator: rest.terminalOperator ?? undefined,
      nameOfOtherCompany: rest.nameOfOtherCompany ?? undefined,
      storagePrice: rest.storagePrice ?? undefined,
      storageMsoFuelCategory: rest.storageMsoFuelCategory ?? undefined,
      storagePriceOffered: rest.storagePriceOffered ?? undefined,
      storagePriceThatWouldBeOffered: rest.storagePriceThatWouldBeOffered ?? undefined,
      typeOfArrangementTxt: rest.typeOfArrangementTxt ?? undefined,           // add this
      typeOfArrangementEntitledCapacityTxt: rest.typeOfArrangementEntitledCapacityTxt ?? undefined, // add this too — same issue
    };
    return mapped;
  }),
  [data]
);

  const anyDeleted = useMemo(() => data.some(d => recordActionFromEnum(d.recordAction) === 'Delete'), [data]);
  const anyInvalid = useMemo(() => dataView.some(v => Object.keys(v.validations).length > 0), [dataView]);

  return useMemo(() => ({
    addStorage,
    anyInvalid,
    deleteAllStoragesForProduct,
    deleteStorage,
    hasChanges: changeCtrl.hasChanges || anyDeleted,
    requestEntities,
    updateStorage,
    updateStorages: dataView,
  }), [addStorage, anyInvalid, deleteAllStoragesForProduct, deleteStorage,
      changeCtrl.hasChanges, anyDeleted, requestEntities, updateStorage, dataView]);
}

export default useMsoStockholdingStorages;
export type UseMsoStockholdingStorages = ReturnType<typeof useMsoStockholdingStorages>;